import { useEffect, useRef } from 'react';
import { API_URL } from '../config/api';

/**
 * ConversionTracking component
 * 
 * Fires conversion events to Google Ads and Meta Pixel when a booking is completed.
 * Uses backend flag as primary deduplication (works across devices).
 * Falls back to localStorage for same-device deduplication.
 */
const ConversionTracking = ({ 
  booking, 
  organization,
  eventType = 'purchase' // 'purchase', 'initiate_checkout', 'add_to_cart'
}) => {
  const hasFired = useRef(false);
  
  useEffect(() => {
    // Only fire once per component mount
    if (hasFired.current) return;
    if (!booking || !organization) return;
    
    const confirmationCode = booking.confirmationCode || booking.confirmation_code;
    
    // Only fire for purchase events when payment is confirmed
    // Includes all "paid" terminal states
    if (eventType === 'purchase') {
      const paymentStatus = booking.paymentStatus || booking.payment_status;
      const bookingStatus = booking.status;
      const paidStatuses = ['paid', 'partial', 'deposit_paid', 'completed', 'refunded', 'partial_refund'];
      const isPaid = paidStatuses.includes(paymentStatus) || bookingStatus === 'confirmed';
      if (!isPaid) {
        return;
      }
    }
    
    // Synchronous guard BEFORE any async work — StrictMode double-invokes and
    // booking/org identity changes must not double-fire the purchase.
    hasFired.current = true;

    // Dedupe: skip if this purchase already fired — same browser via
    // localStorage (booking-success.html sets the same key), cross-device via
    // the backend conversion_tracked flag (set by mark-conversion).
    const checkAndFireConversion = async () => {
      if (localStorage.getItem(`conversion_tracked_${confirmationCode}`)) return;
      try {
        const res = await fetch(`${API_URL}/v2/public/bookings/${confirmationCode}/conversion-status`);
        if (res.ok) {
          const status = await res.json();
          if (status.alreadyTracked) return;
        }
      } catch (err) {
        // Fail open — localStorage still guards same-browser refires
      }
      // Claim the key BEFORE firing so concurrent instances can't race
      try { localStorage.setItem(`conversion_tracked_${confirmationCode}`, Date.now().toString()); } catch (e) { /* fire anyway */ }
      await fireConversionEvents();
    };
    
    const fireConversionEvents = async () => {
      const value = booking.paidAmount || booking.amountPaid || booking.amount_paid || booking.totalAmount || booking.totalPrice || booking.total_price || 0;
      const currency = booking.currency || organization.currency || 'USD';
      const transactionId = confirmationCode;
      const serviceName = booking.serviceName || booking.service_name || '';
      
      // Google Ads Conversion Tracking
      const googleAdsId = organization.googleAdsId || organization.google_ads_id;
      const googleAdsLabel = organization.googleAdsLabel || organization.google_ads_label;
      
      if (googleAdsId && googleAdsLabel) {
        try {
          // Load gtag if not already loaded
          if (!window.gtag) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`;
            document.head.appendChild(script);
            
            window.dataLayer = window.dataLayer || [];
            window.gtag = function() { window.dataLayer.push(arguments); };
            window.gtag('js', new Date());
            window.gtag('config', googleAdsId);
          }
          
          // Fire conversion event
          window.gtag('event', 'conversion', {
            'send_to': `${googleAdsId}/${googleAdsLabel}`,
            'value': value,
            'currency': currency,
            'transaction_id': transactionId
          });
        } catch (error) {
          console.error('[Conversion] Google Ads error:', error);
        }
      }

      // GA4 Tracking — fires to the operator's own GA4 property
      const ga4Id = organization.ga4MeasurementId || organization.google_analytics_id;

      if (ga4Id) {
        try {
          window.dataLayer = window.dataLayer || [];
          if (!window.gtag) {
            window.gtag = function() { window.dataLayer.push(arguments); };
          }
          if (!window[`_ga4Loaded_${ga4Id}`]) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
            document.head.appendChild(script);
            window.gtag('js', new Date());
            // No debug_mode — events should appear in standard GA4 reports
            window.gtag('config', ga4Id);
            window[`_ga4Loaded_${ga4Id}`] = true;
          }
          window.gtag('event', 'purchase', {
            transaction_id: transactionId,
            value: value,
            currency: currency,
            send_to: ga4Id,
            items: [{
              item_id: booking.serviceId || booking.service_id || transactionId,
              item_name: serviceName,
              price: value,
              quantity: booking.partySize || booking.party_size || 1,
            }],
          });
        } catch (error) {
          console.error('[Conversion] GA4 error:', error);
        }
      }
      
      // Meta Pixel Tracking
      const metaPixelId = organization.metaPixelId || organization.meta_pixel_id;
      
      if (metaPixelId) {
        try {
          // Load Meta Pixel if not already loaded
          if (!window.fbq) {
            window.fbq = function() {
              window.fbq.callMethod ? 
                window.fbq.callMethod.apply(window.fbq, arguments) : 
                window.fbq.queue.push(arguments);
            };
            window.fbq.push = window.fbq;
            window.fbq.loaded = true;
            window.fbq.version = '2.0';
            window.fbq.queue = [];
            
            const script = document.createElement('script');
            script.async = true;
            script.src = 'https://connect.facebook.net/en_US/fbevents.js';
            document.head.appendChild(script);
            
            window.fbq('init', metaPixelId);
          }
          
          // Fire purchase ONLY to the operator's pixel (trackSingle, never
          // broadcast) — a broadcast here would leak an un-ID'd Purchase onto
          // the BookingFlows pixel and evade browser/CAPI deduplication.
          window.fbq('trackSingle', metaPixelId, 'Purchase', {
            value: value,
            currency: currency,
            content_name: serviceName,
            content_type: 'service',
            content_ids: [transactionId]
          });
        } catch (error) {
          console.error('[Conversion] Meta Pixel error:', error);
        }
      }
      
      // Google Tag Manager — load the operator's container + push purchase to the dataLayer
      const gtmId = organization.gtmContainerId || organization.gtm_container_id;

      if (gtmId) {
        try {
          window.dataLayer = window.dataLayer || [];
          if (!window[`_gtmLoaded_${gtmId}`]) {
            window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
            document.head.appendChild(script);
            window[`_gtmLoaded_${gtmId}`] = true;
          }
          window.dataLayer.push({
            event: 'purchase',
            transaction_id: transactionId,
            value: value,
            currency: currency,
            item_name: serviceName,
          });
        } catch (error) {
          console.error('[Conversion] GTM error:', error);
        }
      }

      // Mark as tracked
      hasFired.current = true;
      
      // Save to localStorage (backup)
      const conversionKey = `conversion_tracked_${confirmationCode}`;
      localStorage.setItem(conversionKey, Date.now().toString());
      
      // Mark in backend (primary deduplication for cross-device)
      try {
        const resp = await fetch(`${API_URL}/v2/public/bookings/${confirmationCode}/mark-conversion`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: 'ticket_page' })
        });
        if (!resp.ok) console.warn('[Conversion] mark-conversion returned', resp.status);
      } catch (err) {
        // Silent fail - localStorage is backup
      }
    };
    
    checkAndFireConversion();
    
  }, [booking, organization, eventType]);
  
  // This component doesn't render anything visible
  return null;
};

export default ConversionTracking;
